import type { ReactNode } from 'react';
import { PaymentBrandIcon } from '@lecartte/shared-ui/components/PaymentBrandIcon';
import { getCardRowClass, getRadioClass, type SavedCard } from '@/features/subscription/utils/cardUi';
import { PrimaryBadge } from '@/features/subscription/components/PrimaryBadge';

type Props = {
  cards: SavedCard[];
  selectedCardId: string;
  onSelectCard: ({ cardId }: { cardId: string }) => void;
  onUseNewCard: () => void;
  onConfirm: () => void;
  isPending: boolean;
  error?: string | null;
  header: ReactNode;
  confirmLabel: string;
  footer?: ReactNode;
};

export function SavedCardSelector({
  cards,
  selectedCardId,
  onSelectCard,
  onUseNewCard,
  onConfirm,
  isPending,
  error,
  header,
  confirmLabel,
  footer,
}: Props) {
  return (
    <div className="flex flex-col gap-5">
      {header}

      <div className="space-y-2" data-testid="saved-card-list">
        {cards.map((c) => (
          <button
            key={c.id}
            type="button"
            data-testid={`saved-card-row-${c.id}`}
            onClick={() => onSelectCard({ cardId: c.id })}
            className={getCardRowClass({ isSelected: selectedCardId === c.id })}
          >
            <PaymentBrandIcon brand={c.creditCardBrand} size="sm" />
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-900">•••• {c.creditCardNumber}</p>
              <p className="text-xs text-slate-500 uppercase">{c.holderName}</p>
            </div>
            {c.isPrimary && <PrimaryBadge />}
            <div className={getRadioClass({ isSelected: selectedCardId === c.id })} />
          </button>
        ))}
      </div>

      <button
        type="button"
        data-testid="saved-card-use-new"
        onClick={onUseNewCard}
        className="flex items-center justify-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-700 transition"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
        Usar outro cartão
      </button>

      {footer}

      {error && (
        <p data-testid="saved-card-error" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
      )}

      <button
        type="button"
        data-testid="saved-card-confirm"
        disabled={isPending || !selectedCardId}
        onClick={onConfirm}
        className="button w-full justify-center"
      >
        {isPending ? 'Processando...' : confirmLabel}
      </button>
    </div>
  );
}
